import {Component, Input, OnInit} from '@angular/core';
import {debounceTime} from 'rxjs/operators';
import {BasQuarterModel} from '../../../models/widgets/employment-details-model';
import {FormBuilderService} from '../../../services';
import {WizardWidgetComponent} from '../wizard-widget/wizard-widget.component';
import Decimal from 'decimal.js';
import {AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {DataHandlerService} from '../../../services/data-handler.service';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatOptionModule} from '@angular/material/core';
import {MomentDateAdapter} from '@angular/material-moment-adapter';
import {MatSelectModule} from '@angular/material/select';
import {ValidationPartialComponent} from '../partials/validation-partial.component';
import {MatInputModule} from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {NgIf, NgFor} from '@angular/common';
import {
  MatInputCommifiedDirective
} from '../../../../../firstmac-material-lib/projects/fmc-elements/src/lib/commified/mat-input-commified-directive';
import * as moment from 'moment';
import {MatIcon} from '@angular/material/icon';
import {MatTooltip} from '@angular/material/tooltip';

const BAS_DATE_FORMATS = {
  parse: { dateInput: 'MMM-YY' },
  display: {
    dateInput: 'MMM-YY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

const ATO_QUARTER_MONTHS = [2, 5, 8, 11]; // Mar=2, Jun=5, Sep=8, Dec=11 (0-indexed)

@Component({
  selector: 'app-bas-activity-details',
  templateUrl: './bas-activity-details.component.html',
  standalone: true,
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'en-AU' },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: BAS_DATE_FORMATS },
  ],
  imports: [
    NgIf, NgFor, FormsModule, ReactiveFormsModule,
    MatFormFieldModule, MatInputModule,
    ValidationPartialComponent,
    MatSelectModule, MatOptionModule,
    MatDatepickerModule,
    MatInputCommifiedDirective, MatIcon, MatTooltip
  ]
})
export class BasActivityDetailsComponent extends WizardWidgetComponent implements OnInit {
  @Input() formGroup;
  @Input() isCompany = false;

  public readonly QUARTER_OPTIONS = [2, 4];
  public quarterPeriodLabels: string[] = [];
  public quarterIndices: number[] = [];

  constructor(
    public dataHandlerService: DataHandlerService,
    private formBuilderService: FormBuilderService
  ) {
    super(dataHandlerService);
  }

  ngOnInit() {
    super.ngOnInit();

    if (!this.formGroup.get('basQuarters')) {
      this.formGroup.addControl('basQuarters', new UntypedFormArray([]));
    }
    if (!this.formGroup.get('numberOfQuarters')) {
      this.formGroup.addControl('numberOfQuarters', new UntypedFormControl(2));
    }

    const mostRecentBasPeriod = this.formGroup.get('mostRecentBasPeriod');
    if (mostRecentBasPeriod) {
      this.quarterPeriodLabels = this.deriveQuarterPeriodLabels(mostRecentBasPeriod.value);
      mostRecentBasPeriod.valueChanges
        .pipe(debounceTime(300))
        .subscribe((date: any) => this.onMostRecentBasPeriodChange(date));
    }

    // If quarters are pre-populated (session restore or prior component instance), sync the
    // dropdown to match and re-attach valueChanges subscriptions — subscriptions belong to the
    // component instance, so they must be re-wired whenever this component is recreated.
    const existingCount = this.basQuartersArray.length;
    if (existingCount > 0) {
      this.formGroup.get('numberOfQuarters').patchValue(existingCount, {emitEvent: false});
      this.updateQuarterIndices(existingCount);
      this.basQuartersArray.controls.forEach(quarterFg => {
        this.applyQuarterValidators(quarterFg as UntypedFormGroup);
        this.wireQuarterCalcs(quarterFg);
      });
      // Re-snap mostRecentBasPeriod to the quarter-end and re-sync period labels onto
      // existing quarter controls (guards against API-loaded periods in YYYY-MM format).
      if (mostRecentBasPeriod?.value) {
        this.onMostRecentBasPeriodChange(mostRecentBasPeriod.value);
      }
    } else {
      const initialCount = this.formGroup.get('numberOfQuarters').value || 2;
      this.syncQuartersToCount(initialCount);
    }

    this.formGroup.get('numberOfQuarters').valueChanges
      .pipe(debounceTime(200))
      .subscribe((count: number) => this.syncQuartersToCount(count));
  }

  get maxBasPeriod(): moment.Moment {
    const today = moment();
    for (let i = ATO_QUARTER_MONTHS.length - 1; i >= 0; i--) {
      const quarterEnd = moment({ year: today.year(), month: ATO_QUARTER_MONTHS[i] }).endOf('month');
      if (today.isSameOrAfter(quarterEnd)) {
        return quarterEnd;
      }
    }
    return moment({ year: today.year() - 1, month: 11 }).endOf('month');
  }

  get basQuartersArray(): UntypedFormArray {
    return this.formGroup.get('basQuarters') as UntypedFormArray;
  }

  getQuarterControl(index: number, field: string): UntypedFormControl {
    return this.basQuartersArray.at(index)?.get(field) as UntypedFormControl;
  }

  getQuarterFg(index: number): UntypedFormGroup {
    return this.basQuartersArray.at(index) as UntypedFormGroup;
  }

  private readonly directorSalariesValidator = (fg: AbstractControl): ValidationErrors | null => {
    const directorSalaries = new Decimal(fg.get('directorSalaries')?.value || 0);
    const w1 = new Decimal(fg.get('w1')?.value || 0);
    return directorSalaries.greaterThan(w1) ? { directorSalariesExceedW1: true } : null;
  };

  private updateQuarterIndices(count: number) {
    this.quarterIndices = Array.from({length: count}, (_, i) => i);
  }

  syncQuartersToCount(count: number) {
    const clampedCount = [2, 4].includes(count) ? count : 2;
    const basQuarters = this.basQuartersArray;
    while (basQuarters.length < clampedCount) {
      this.addBasQuarter();
    }
    while (basQuarters.length > clampedCount) {
      basQuarters.removeAt(basQuarters.length - 1);
    }
    this.updateQuarterIndices(clampedCount);
    this.onMostRecentBasPeriodChange(this.formGroup.get('mostRecentBasPeriod')?.value);
  }

  private applyQuarterValidators(fg: UntypedFormGroup) {
    const currencyValidators = [Validators.required, Validators.min(0)];
    ['g1', 'oneA', 'oneB', 'w1', 'directorSalaries'].forEach(field => {
      const ctrl = fg.get(field) as UntypedFormControl;
      if (ctrl) {
        ctrl.setValidators(currencyValidators);
        ctrl.updateValueAndValidity({emitEvent: false});
      }
    });
    const salesCtrl = fg.get('salesIncludeGst') as UntypedFormControl;
    if (salesCtrl) {
      salesCtrl.setValidators(Validators.required);
      salesCtrl.updateValueAndValidity({emitEvent: false});
    }
    fg.updateValueAndValidity({emitEvent: false});
  }

  private addBasQuarter() {
    const basQuarters = this.basQuartersArray;
    const fg = this.formBuilderService.convertObjectToFormGroup(new BasQuarterModel());
    this.applyQuarterValidators(fg);

    const idx = basQuarters.length;
    if (this.quarterPeriodLabels[idx]) {
      fg.get('period').patchValue(this.quarterPeriodLabels[idx], {emitEvent: false});
    }

    this.wireQuarterCalcs(fg);
    basQuarters.push(fg);
  }

  /** Attach valueChanges subscriptions that keep quarterlyIncomeTotal in sync. Called both
   *  when a new quarter is added and when the component is recreated over existing quarters. */
  private wireQuarterCalcs(quarterFg) {
    quarterFg.setValidators(this.directorSalariesValidator);
    quarterFg.updateValueAndValidity({emitEvent: false});
    ['g1', 'salesIncludeGst', 'oneA', 'oneB', 'w1'].forEach(field => {
      quarterFg.get(field)?.valueChanges
        .pipe(debounceTime(300))
        .subscribe(() => this.calculateBasQuarterlyTotal(quarterFg));
    });
  }

  /** Snap picked date to nearest ATO quarter-end and generate 4 Mon-YY labels backwards. */
  deriveQuarterPeriodLabels(date: any): string[] {
    if (!date) { return []; }
    const m = moment(date);
    if (!m.isValid()) { return []; }

    const month = m.month();
    let quarterMonth = ATO_QUARTER_MONTHS.find(qm => qm >= month);
    if (quarterMonth === undefined) {
      quarterMonth = ATO_QUARTER_MONTHS[0];
    }

    const quarterEnd = moment({year: m.year(), month: quarterMonth}).endOf('month');
    const labels: string[] = [];
    for (let i = 0; i < 4; i++) {
      labels.push(moment(quarterEnd).subtract(i * 3, 'months').format('MMM-YY'));
    }
    return labels;
  }

  /** Update period labels and sync existing quarter period controls when the most recent BAS date changes. */
  onMostRecentBasPeriodChange(date: any) {
    this.quarterPeriodLabels = this.deriveQuarterPeriodLabels(date);
    const basQuarters = this.basQuartersArray;
    basQuarters.controls.forEach((quarterFg, idx) => {
      if (this.quarterPeriodLabels[idx] !== undefined) {
        quarterFg.get('period').patchValue(this.quarterPeriodLabels[idx], {emitEvent: false});
      }
    });
  }

  /** Total Income for Quarter = Total Sales Net of GST - (1B / 0.1) - (W1 * 3)
   *  Total Sales Net of GST = G1 - 1A (when sales include GST) or G1 (when sales exclude GST).
   */
  calculateBasQuarterlyTotal(quarterFg) {
    try {
      const g1 = new Decimal(quarterFg.get('g1').value || 0);
      const salesIncludeGst = quarterFg.get('salesIncludeGst').value === true;
      const oneA = new Decimal(quarterFg.get('oneA').value || 0);
      const oneB = new Decimal(quarterFg.get('oneB').value || 0);
      const w1 = new Decimal(quarterFg.get('w1').value || 0);

      const netSales = salesIncludeGst ? g1.minus(oneA) : g1;
      const total = netSales.minus(oneB.dividedBy(0.1)).minus(w1.times(3)).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber();
      quarterFg.get('quarterlyIncomeTotal').patchValue(total, {emitEvent: false});
    } catch (e) {
      console.log('Error calculating BAS quarterly total', e);
    }
  }
}
